<template>
  <!-- Caller info (hidden when dialpad is open) -->
  <div
    v-if="!showDtmfPad || !showHoldMute"
    class="flex flex-col items-center gap-2 relative flex-1 min-h-0 w-full overflow-hidden"
  >
    <img :src="require('@/assets/user.png')" alt="logo" class="w-22 h-22" />
    <p v-if="isAnswered" class="text-white text-xs">
      {{ durationFormat(callDuration) }}
    </p>
    <p v-if="sessionPeer" class="text-white text-xl">{{ sessionPeer }}</p>
    <!-- Where the call came from. An agent handling both outbound campaign calls
         and inbound queue calls needs to know which one is ringing before they
         answer, so this sits directly under the number. -->
    <!-- The label is translated HERE rather than in the computed: a computed
         caches, so a $t() inside it can hold on to the locale it first ran
         under. In the template it re-renders with the language. -->
    <div v-if="callSource" class="call-source">
      <v-icon size="14">{{ callSource.icon }}</v-icon>
      <span v-if="callSource.kind === 'campaign'">
        {{
          callSource.campaign
            ? $t("calls.phone.campaign_call_named", { campaign: callSource.campaign })
            : $t("calls.phone.campaign_call")
        }}
      </span>
      <span v-else>
        {{ $t("calls.phone.queue_call", { queue: callSource.queue }) }}
      </span>
    </div>
    <div
      v-if="isWhispering && type === 1"
      class="px-2 py-1 text-xs flex items-center gap-1"
    >
      <v-icon class="blink-icon text-yellow-500" size="16"
        >mdi-microphone</v-icon
      >
      <span class="text-white">{{
        $t("calls.phone.whispering") || "Whispering"
      }}</span>
    </div>
  </div>
  <!-- DTMF Dialpad (takes caller info's place when open) -->
  <div
    v-else
    class="flex flex-col items-center w-full flex-1 min-h-0 px-2 py-1"
    style="direction: ltr;"
  >
    <input
      v-model="dtmfInput"
      type="text"
      readonly
      class="w-full p-1 mb-2 rounded text-center text-base text-white bg-transparent border border-white/20 focus:outline-none flex-shrink-0"
      placeholder=""
    />
    <div class="grid grid-cols-3 grid-rows-4 gap-1.5 w-full max-w-[200px] flex-1 min-h-0">
      <button
        v-for="key in dtmfKeys"
        :key="key"
        class="rounded-full bg-coal-blue text-white text-base hover:bg-coal-blue-hover active:scale-95 transition-transform min-h-0 min-w-0"
        @click="pressDtmfKey(key)"
      >
        {{ key }}
      </button>
    </div>
  </div>
  <div class="flex justify-between items-center w-full gap-2 p-1 flex-shrink-0">
    <div
      v-if="showAnswer && hasPermisson(phonePermission)"
      class="flex flex-col items-center"
    >
      <Button
        class="bg-green-800 hover:bg-green-800-hover p-2 text-white rounded-full text-sm"
        @click="setAnswerRequest"
        rounded
      >
        <v-icon>mdi-phone-outline</v-icon>
      </Button>
      <p class="text-white text-xs">{{ $t("calls.phone.answer_btn") }}</p>
    </div>
    <div v-if="!showDial" class="flex flex-col items-center">
      <Button
        class="bg-red p-2 text-white rounded-full text-sm"
        @click="hangupBTNHandler"
        rounded
      >
        <v-icon>mdi-phone-hangup-outline</v-icon>
      </Button>
      <p class="text-white text-xs">{{ $t("calls.phone.hangup_btn") }}</p>
    </div>
    <div v-if="showHoldMute" class="flex flex-col items-center">
      <div class="relative">
        <Button
          :class="[
            'p-2 text-white rounded-full text-sm',
            isMute
              ? 'bg-coal-blue-hover hover:bg-coal-blue '
              : 'bg-coal-blue  hover:bg-coal-blue-hover',
          ]"
          :disabled="isHolding"
          @click="setMuteRequest"
          rounded
        >
          <v-icon>{{ isMute ? "mdi-microphone-off" : "mdi-microphone" }}</v-icon>
        </Button>
        <!-- Mic-device picker toggle — opens the selector as a popup so the
             widget layout never shifts. Hidden by default (agents normally have
             a working mic) yet one click away, right where you look when the
             mic misbehaves. Stays enabled while holding. -->
        <button
          type="button"
          :title="$t('calls.phone.select_mic')"
          class="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-coal-blue-hover hover:bg-coal-blue text-white flex items-center justify-center border border-white/30"
          @click.stop="toggleMicMenu"
        >
          <v-icon size="14">{{
            micMenuOpen ? "mdi-chevron-up" : "mdi-chevron-down"
          }}</v-icon>
        </button>
      </div>
      <p class="text-white text-xs">
        {{ isMute ? $t("calls.phone.unmute_btn") : $t("calls.phone.mute_btn") }}
      </p>
    </div>
    <div v-if="showHoldMute" class="flex flex-col items-center">
      <Button
        :class="[
          'p-2 text-white rounded-full text-sm',
          isHolding
            ? 'bg-coal-blue-hover hover:bg-coal-blue '
            : 'bg-coal-blue  hover:bg-coal-blue-hover',
        ]"
        :disabled="isMute"
        @click="holdBTNHandler"
        rounded
      >
        <v-icon>{{ isHolding ? "mdi-play" : "mdi-pause" }}</v-icon>
      </Button>
      <p class="text-white text-xs">
        {{
          isHolding ? $t("calls.phone.unhold_btn") : $t("calls.phone.hold_btn")
        }}
      </p>
    </div>
    <div v-if="showTransferToQueue" class="flex flex-col items-center">
      <Button
        class="bg-coal-blue hover:bg-coal-blue-hover p-2 text-white rounded-full text-sm"
        @click="showTransferDialog = true"
        rounded
      >
        <v-icon>mdi-phone-forward</v-icon>
      </Button>
      <p class="text-white text-xs">
        {{ $t("calls.phone.transfer_btn") || "Transfer" }}
      </p>
    </div>
    <div v-if="showHoldMute" class="flex flex-col items-center">
      <Button
        :class="[
          'p-2 text-white rounded-full text-sm',
          showDtmfPad
            ? 'bg-coal-blue-hover hover:bg-coal-blue'
            : 'bg-coal-blue hover:bg-coal-blue-hover',
        ]"
        @click="showDtmfPad = !showDtmfPad"
        rounded
      >
        <v-icon>mdi-dialpad</v-icon>
      </Button>
      <p class="text-white text-xs">{{ $t("calls.phone.dialpad_btn") }}</p>
    </div>
  </div>
  <!-- Microphone (audio input) picker popup. Switching hot-swaps the live
       call's mic (no redial), so a headset plugged in after answering works. -->
  <Popover
    ref="micMenu"
    class="mic-popover"
    appendTo="body"
    @show="micMenuOpen = true"
    @hide="micMenuOpen = false"
  >
    <div class="flex flex-col gap-1 min-w-[220px] max-w-[280px]">
      <p class="text-xs font-semibold text-white/70 px-2 pt-1 flex items-center gap-1">
        <v-icon size="14">mdi-microphone-settings</v-icon>
        {{ $t("calls.phone.select_mic") }}
      </p>
      <button
        v-for="(dev, index) in audioInputs"
        :key="dev.deviceId || index"
        type="button"
        class="text-start text-sm text-white px-2 py-2 rounded flex items-center gap-2 hover:bg-coal-blue-hover disabled:opacity-60 w-full"
        :class="{ 'bg-coal-blue-hover font-semibold': dev.deviceId === selectedMicId }"
        :disabled="micSwitching"
        @click="selectMic(dev.deviceId)"
      >
        <v-icon size="16">{{
          dev.deviceId === selectedMicId ? "mdi-check-circle" : "mdi-microphone"
        }}</v-icon>
        <span class="truncate">{{ micLabel(dev, index) }}</span>
      </button>
      <p v-if="!audioInputs.length" class="text-xs text-white/60 px-2 py-2">
        {{ $t("calls.phone.no_mics") }}
      </p>
    </div>
  </Popover>
  <Dialog
    v-model:visible="showTransferDialog"
    :header="$t('calls.phone.transfer_to_queue')"
    :style="{ width: '400px' }"
    modal
    :closable="true"
    :closeOnEscape="true"
  >
    <div class="flex flex-col gap-4">
      <Select
        v-model="forwardToSkill"
        :options="domainSkills"
        :optionLabel="'titles.' + currentLocal"
        optionValue="name"
        :placeholder="$t('calls.phone.select_queue')"
        class="w-full"
      />
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('calls.phone.cancel_transfer')"
          severity="secondary"
          text
          @click="showTransferDialog = false"
        />
        <Button
          :label="$t('calls.phone.transfer_btn')"
          :disabled="!forwardToSkill"
          :loading="transferLoading"
          @click="transferCall"
        />
      </div>
    </div>
  </Dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import moment from "moment";
import { Button, Dialog, Select, Popover } from "primevue";
import WebRTCService from "@/services/WebRTCService";
// The i18n instance directly, not `this.$i18n`: with legacy:false the injected
// `$i18n.locale` is a ref, so it can't be used as an object key.
import i18n from "@/i18n";

export default {
  props: ["call"],
  components: {
    Button,
    Dialog,
    Select,
    Popover,
  },
  data() {
    return {
      callDuration: 0,
      durationTimer: null,
      // Where the current call came from, worked out once and kept for the life
      // of the call — see the currentCall watcher.
      callSource: null,
      callSourceLinkedid: null,
      acwTimerDuration: 0,
      acwInterval: null,
      forwardToSkill: "",
      transferLoading: false,
      showTransferDialog: false,
      acwLoading: false,
      hangupLoading: false,
      selectedCallType: null,
      currentSentiment: "neutral",
      storedUniqueHoldId: null,
      showDtmfPad: false,
      dtmfInput: "",
      dtmfKeys: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"],
      audioInputs: [],
      selectedMicId: null,
      micSwitching: false,
      micMenuOpen: false,
      boundDeviceChange: null,
    };
  },
  computed: {
    ...mapGetters([
      "domainSkills",
      "isAnswered",
      "isHolding",
      "isMute",
      "sessionPeer",
      "isIncoming",
      "isOutgoing",
      // "callStart",
      "transferPermission",
      "domain",
      "sipPermission",
      "phonePermission",
      "permissions",
      "hangupRequest",
      "inCall",
      "currentCall",
      "acwSettings",
      "acwStatus",
      "acwReason",
      "acwCRM",
      "isPhoneDrawn",
      "sentiment",
      "isWhispering",
      "type",
    ]),
    // Campaign vs queue, from the GetActiveCall payload. `IsCampaignCall` is the
    // dialer's flag — it arrives as "" on a non-campaign call, so truthiness is
    // the test. Otherwise a Dst that names one of this domain's queues means an
    // inbound queue call. Anything else (a direct dial, an internal extension)
    // gets no badge rather than a guessed one.
    // What the CURRENT payload says about the call's origin. This can go back to
    // null as the call progresses — once answered, the bridged leg no longer
    // looks like an inbound queue leg — so the watcher latches the first answer
    // instead of reading this directly. See callSource in data().
    detectedCallSource() {
      if (!this.currentCall) return null;
      if (this.currentCall.IsCampaignCall) {
        // CampaignName is only included when it has one; that way merging a
        // later reading can't blank a name we already showed.
        const campaign = String(this.currentCall.CampaignName ?? "").trim();
        return {
          kind: "campaign",
          icon: "mdi-bullhorn-outline",
          ...(campaign ? { campaign } : {}),
        };
      }
      const queue = this.callQueueTitle;
      if (queue) {
        return { kind: "queue", icon: "mdi-account-group-outline", queue };
      }
      return null;
    },
    // There is no queue-name field on the call: the queue is `Dst`, which holds
    // a skill NAME (e.g. "7766") — the same value the ACW check below matches
    // against acwSettings.skills.
    //
    // The skills list only supplies the human title; it deliberately does NOT
    // decide whether this is a queue call. `domainSkills` is fetched by the
    // PAGE, not by the call, so it can still be null while the phone is ringing
    // — gating on it is why the badge used to appear only after answering.
    // The structural test is that the call is addressed TO the queue: CallerID
    // and Dst both carry the queue, while SourceID holds the caller's number. A
    // direct extension call has CallerID != Dst and so gets no badge.
    callQueueTitle() {
      const call = this.currentCall || {};
      const dst = String(call.Dst ?? "").trim();
      if (!dst) return "";
      const skills = Array.isArray(this.domainSkills) ? this.domainSkills : [];
      const skill = skills.find((s) => String(s?.name ?? s).trim() === dst);
      if (skill) {
        const locale = i18n.global.locale.value;
        return skill.titles?.[locale] || skill.titles?.en || skill.name || dst;
      }
      // Skills not loaded yet: still badge it, labelled with the raw queue
      // number, and let the title fill in once they arrive.
      return String(call.CallerID ?? "").trim() === dst ? dst : "";
    },
    sessionTitle() {
      if (this.isAnswered && !this.isHolding && !this.isMute) {
        return this.$t("calls.phone.session_title.in_line");
      } else if (this.isIncoming && !this.isAnswered) {
        return this.$t("calls.phone.session_title.incoming");
      } else if (this.isOutgoing && !this.isAnswered) {
        return this.$t("calls.phone.session_title.dialing");
      } else if (this.isAnswered && this.isHolding && !this.isMute) {
        return this.$t("calls.phone.session_title.holding");
      } else if (this.isAnswered && !this.isHolding && this.isMute) {
        return this.$t("calls.phone.session_title.muting");
      } else {
        return this.$t("calls.phone.session_title.phone");
      }
    },
    sessionIcon() {
      if (this.isAnswered) {
        return "mdi-phone-in-talk";
      } else if (this.isIncoming && !this.isAnswered) {
        return "mdi-phone-incoming";
      } else if (this.isOutgoing && !this.isAnswered) {
        return "mdi-phone-outgoing";
      } else {
        return "mdi-phone-classic";
      }
    },
    callType() {
      if (!this.inCall) {
        return { type: "", color: "bg-teal-lighten-2" };
      } else if (this.isHolding) {
        return {
          type: this.callType
            ? this.callType.type
              ? this.callType.type
              : ""
            : "",
          color: "bg-warning",
        };
      } else if (this.isMute) {
        return {
          type: this.callType
            ? this.callType.type
              ? this.callType.type
              : ""
            : "",
          color: "bg-lime",
        };
      } else if (this.isIncoming) {
        return {
          type: this.$t("reports.filters.incoming"),
          color: "bg-green-lighten-1",
        };
      } else if (this.isOutgoing) {
        return {
          type: this.$t("reports.filters.outgoing"),
          color: "bg-blue-darken-1",
        };
      }
      //  else if (this.call.CallType == 3) {
      //   return {
      //     type: this.$t("reports.filters.internal"),
      //     color: "bg-cyan-lighten-5",
      //   };
      // }
      else {
        return { type: "", color: "bg-grey-lighten-4" };
      }
    },
    acwDurationFormat() {
      return moment.unix(this.acwTimerDuration).format("mm:ss");
    },

    isACW() {
      // console.log("isAcw 1");
      if (
        this.inCall ||
        !this.acwSettings?.enabled ||
        !this.currentCall ||
        !this.currentCall?.isAnswered ||
        !this.acwSettings?.skills?.includes(this.currentCall?.Dst)
      ) {
        return false;
      }
      // console.log("isAcw 2");
      if (
        this.acwStatus &&
        this.acwStatus?.channel != this.currentCall.channel
      ) {
        return false;
      }
      // console.log("isAcw 3");
      let allSet = true;
      if (this.acwStatus) {
        this.acwSettings.after_work.array.forEach((element) => {
          if (!this.acwStatus.isSet.includes(element)) {
            allSet = false;
          }
        });
      } else {
        allSet = false;
      }
      // console.log("isAcw 3");
      if (allSet) {
        return false;
      }
      // console.log("isAcw 4");
      return true;
    },
    showDial() {
      return !this.inCall;
    },
    showAnswer() {
      return this.inCall && !this.isAnswered && this.isIncoming;
    },
    showHoldMute() {
      return this.inCall && this.isAnswered;
    },
    showTransferToQueue() {
      return (
        this.inCall &&
        this.isAnswered &&
        this.hasPermisson(this.transferPermission) &&
        this.domain !== "go"
      );
    },
    inMonitorMode() {
      return this.sessionPeer?.toLowerCase().includes("monitor");
    },
    currentLocal() {
      return this.$vuetify.locale.current;
    },
  },
  methods: {
    ...mapActions([
      "SetMessageDialog",
      "HangupCall",
      "TransferCall",
      "SetACWTime",
      "SetCallOnHold",
      "MonitorAction",
    ]),
    ...mapMutations([
      "setIncall",
      "setAnswerRequest",
      "setHangupRequest",
      "setHoldRequest",
      "setMuteRequest",
      "setCurrentCall",
      "setACWStatus",
      "setInAcw",
      "setIsWhispering",
      "setWhisperedAgent",
      "setWhisperedChannel",
    ]),
    durationFormat(duration) {
      return moment.unix(duration).format("mm:ss");
    },
    setCallDuration() {
      this.callDuration = moment().diff(
        moment.unix(+this.currentCall.Start),
        "seconds"
      );
      this.durationTimer = setInterval(() => {
        this.callDuration++;
      }, 1000);
    },
    hasPermisson(permission) {
      return this.permissions.includes(permission);
    },
    async hangupBTNHandler() {
      console.log("hangupBTNHandler called");
      // If this is an admin call (admin whispering to agent), send action 5 to stop whisper before hanging up
      const agentStatusPermission = this.$store.getters.agentStatusPermission;
      const isAdminCall = !this.hasPermisson(agentStatusPermission);
      const whisperedAgent = this.$store.getters.whisperedAgent;
      const whisperedChannel = this.$store.getters.whisperedChannel;

      if (isAdminCall && whisperedChannel && whisperedAgent) {
        try {
          await this.MonitorAction({
            action: 5,
            channel: whisperedChannel,
            agent: whisperedAgent,
            workstatus: 1,
          });
        } catch (err) {
          console.log("Error stopping whisper on hangup:", err);
          // Continue with hangup even if whisper stop fails
        }
      }

      if (this.hasPermisson(this.phonePermission)) {
        this.setCurrentCall({ ...this.currentCall, End: moment().unix() });
        this.setHangupRequest(!this.hangupRequest);
      } else if (this.hasPermisson(this.sipPermission)) {
        this.hangupLoading = true;
        try {
          const resp = await this.HangupCall(this.currentCall.Channel);
          this.hangupLoading = false;
          if (!resp.success) {
            this.SetMessageDialog({ show: true, message: resp.message });
          } else {
            console.log("My hangup call", resp);
            this.setIncall(false);
          }
        } catch (err) {
          console.log("My Hangup Err", err);
          this.hangupLoading = false;
          let message = err.message;
          if (err.response) {
            message = err.response.data.message;
          }
          this.SetMessageDialog({ show: true, message: message });
        }
      }
    },
    async holdBTNHandler() {
      if (!this.currentCall) {
        this.SetMessageDialog({
          show: true,
          message: this.$t("calls.phone.no_active_call") || "No active call",
        });
        return;
      }

      const isSettingHold = !this.isHolding;
      const payload = {
        is_hold: isSettingHold,
        callid: this.currentCall.Linkedid,
        queue: this.currentCall.Dst,
        caller: this.currentCall.SourceID,
        request_time: moment().unix().toString(),
      };

      // Only include unique_hold_id when setting hold to false (using stored value)
      if (!isSettingHold && this.storedUniqueHoldId) {
        payload.unique_hold_id = this.storedUniqueHoldId;
      }

      try {
        const resp = await this.SetCallOnHold(payload);
        if (!resp.success) {
          this.SetMessageDialog({ show: true, message: resp.message });
          return;
        }

        // Store unique_hold_id from response when setting hold to true
        if (isSettingHold && resp.result?.unique_hold_id) {
          this.storedUniqueHoldId = resp.result.unique_hold_id;
        } else if (!isSettingHold) {
          // Clear stored unique_hold_id when unholding
          this.storedUniqueHoldId = null;
        }

        this.setHoldRequest(!this.isHolding);
      } catch (err) {
        let message = err.message;
        if (err.response) {
          message = err.response.data?.message || err.response.statusText;
        }
        this.SetMessageDialog({ show: true, message });
      }
    },
    async transferCall() {
      this.transferLoading = true;
      try {
        // console.log("My transfer call", this.currentCall);
        const resp = await this.TransferCall({
          channel: this.currentCall.CallerChannel,
          skill: this.forwardToSkill,
          context: "weqaa_incoming",
        });
        this.transferLoading = false;
        this.showTransferDialog = false;
        if (!resp.success) {
          this.SetMessageDialog({ show: true, message: resp.message });
        } else {
          console.log("My transfer call", resp);
          this.setIncall(false);
        }
      } catch (err) {
        console.log("Error: ", err);
        this.transferLoading = false;
        this.showTransferDialog = false;
        let message = err.message;
        if (err.response) {
          message = err.response.data.message;
        }
        this.SetMessageDialog({ show: true, message: message });
      }
    },
    setACWDuration() {
      if (!this.acwSettings || !this.currentCall?.End) {
        return;
      }
      const secondsPassed = moment().diff(
        moment.unix(+this.currentCall.End),
        "seconds"
      );

      this.acwTimerDuration = +this.acwSettings.duration - secondsPassed;
      this.acwInterval = setInterval(() => {
        if (this.acwTimerDuration > 0) {
          this.acwTimerDuration--;
        } else {
          this.handleACW();
        }
      }, 1000);
    },
    clear() {
      clearInterval(this.acwInterval);
      this.acwInterval = null;
    },
    async handleACW() {
      if (!this.isACW && !this.acwStatus) {
        this.clear();
        return;
      }
      this.acwLoading = true;
      this.clear();
      try {
        const resp = await this.SetACWTime({
          channel: this.currentCall.Channel,
          start: moment
            .unix(+this.currentCall.Start)
            .format("YYYY-MM-DD HH:mm:ss"),
          callid: this.currentCall.Linkedid,
          acw: +this.acwSettings.duration - this.acwTimerDuration,
        });

        this.acwLoading = false;
        this.acwTimerDuration = 0;
        this.setCurrentCall(null);
        this.setACWStatus(null);
        if (!resp.success) {
          this.SetMessageDialog({ show: true, message: resp.message });
        }
      } catch (err) {
        console.log("Error: ", err);
        this.acwLoading = false;

        let message = err.message;
        if (err.response) {
          message = err.response.data.message;
        }
        this.SetMessageDialog({ show: true, message: message });
      }
    },
    pressDtmfKey(key) {
      this.dtmfInput += key;
      WebRTCService.sendDTMF(key);
    },
    async loadAudioInputs() {
      try {
        this.audioInputs = await WebRTCService.getAudioInputDevices();
        this.selectedMicId = WebRTCService.getCurrentInputDeviceId();
      } catch (err) {
        console.log("Error loading audio inputs:", err);
        this.audioInputs = [];
      }
    },
    toggleMicMenu(event) {
      this.loadAudioInputs();
      this.$refs.micMenu.toggle(event);
    },
    async selectMic(deviceId) {
      if (this.micSwitching) return;
      this.micSwitching = true;
      let switched = false;
      try {
        await WebRTCService.switchAudioInput(deviceId);
        switched = true;
      } catch (err) {
        console.log("Error switching microphone:", err);
        this.SetMessageDialog({
          show: true,
          message: this.$t("calls.phone.mic_switch_error"),
        });
      } finally {
        // Resync with the device actually live on the call — this also reverts
        // the highlight if the switch failed.
        await this.loadAudioInputs();
        this.micSwitching = false;
        // Close the popup once the switch succeeds; keep it open on failure so
        // another device can be tried immediately.
        if (switched && this.$refs.micMenu) {
          this.$refs.micMenu.hide();
        }
      }
    },
    micLabel(dev, index) {
      return dev.label || `${this.$t("calls.phone.default_mic")} ${index + 1}`;
    },
    toggle(event) {
      this.$refs.menu.toggle(event);
    },
    // Keep the best reading of the call's origin. Merging rather than replacing
    // means a detail that only one payload carried — the campaign's name, or the
    // queue's title once domainSkills lands — isn't lost on the next update.
    rememberCallSource() {
      const detected = this.detectedCallSource;
      if (!detected) return;
      this.callSource =
        this.callSource && this.callSource.kind === detected.kind
          ? { ...this.callSource, ...detected }
          : detected;
    },
  },
  watch: {
    // A call's origin doesn't change once it's up, but the payload describing it
    // does: after the answer the bridged leg reports different CallerID/Dst, so
    // recomputing from it would make the badge disappear mid-call. Take the
    // first reading that identifies the call and hold it until the call ends or
    // a different one starts.
    currentCall: {
      handler(call) {
        const linkedid = call?.Linkedid ?? null;
        if (!call || linkedid !== this.callSourceLinkedid) {
          this.callSource = null;
          this.callSourceLinkedid = linkedid;
        }
        this.rememberCallSource();
      },
      immediate: true,
    },
    // domainSkills is fetched separately, so it can land after the badge is
    // already showing the raw queue number — re-read to pick up the name.
    domainSkills() {
      this.rememberCallSource();
    },
    sentiment(value) {
      console.log("My sentiment:::", value);
      this.currentSentiment = value || "neutral";
    },
    inCall(val) {
      // console.log("My incall watch:", val);
      clearInterval(this.durationTimer);
      this.durationTimer = null;
      if (!val) {
        clearInterval(this.durationTimer);
        this.durationTimer = null;
        this.callDuration = 0;
        // Clear stored unique_hold_id when call ends
        this.storedUniqueHoldId = null;
        this.showDtmfPad = false;
        this.dtmfInput = "";
        if (this.$refs.micMenu) this.$refs.micMenu.hide();
        // Clear whispering state when call ends
        this.setIsWhispering(false);
        // Clear whispered agent and channel when call ends
        this.setWhisperedAgent(null);
        this.setWhisperedChannel(null);
        if (this.isACW) {
          if (!this.currentCall?.End) {
            this.setCurrentCall({ ...this.currentCall, End: moment().unix() });
          }
          this.setACWDuration();
        } else {
          this.setCurrentCall(null);
        }
      }
    },
    async isACW(val) {
      if (!val) {
        await this.handleACW();
      }
      this.setInAcw(val);
    },

    isAnswered(val) {
      // console.log("My answered watch:", val);
      if (val) {
        this.setCallDuration();
        this.loadAudioInputs(); // populate the mic dropdown with the live device
      }
    },
    isPhoneDrawn(val) {
      if (!val) {
        this.clear();
      }
    },
  },
  unmounted() {
    this.clear();
    clearInterval(this.durationTimer);
    this.durationTimer = null;
    if (navigator.mediaDevices && this.boundDeviceChange) {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        this.boundDeviceChange
      );
      this.boundDeviceChange = null;
    }
  },
  mounted() {
    this.clear();
    if (this.currentCall?.Start && this.isAnswered && this.callDuration == 0) {
      this.setCallDuration();
    }
    if (this.isACW) {
      this.setACWDuration();
    }
    // If we mount mid-call (already answered), populate the mic dropdown now.
    if (this.showHoldMute) {
      this.loadAudioInputs();
    }
    // Keep the mic list fresh so a headset plugged in mid-call shows up (and is
    // reflected as active after a switch) without reopening the widget.
    if (navigator.mediaDevices) {
      this.boundDeviceChange = () => this.loadAudioInputs();
      navigator.mediaDevices.addEventListener(
        "devicechange",
        this.boundDeviceChange
      );
    }
  },
};
</script>
<style scoped>
/* Where the call came from. Sits on the dark call card, so it's a translucent
   white pill rather than a coloured one. */
.call-source {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 11px;
  line-height: 1.6;
}
.call-source span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.blink-icon {
  animation: blink 1s infinite;
}
</style>

<!-- The mic popover is teleported to <body>, so it can't be reached by scoped
     styles — theme it globally to match the dark call widget. -->
<style>
.mic-popover.p-popover {
  background: var(--p-coal-blue);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
}
.mic-popover .p-popover-content {
  padding: 0.5rem;
}
/* The theme's popover arrow (::before/::after triangles) doesn't position
   reliably here — the NavBar status popover fights the same pseudo-elements.
   Hide them; the panel reads fine as a plain floating card. */
.mic-popover.p-popover:before,
.mic-popover.p-popover:after {
  display: none;
}
</style>
